import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

/** Keeps this panel's remembered views out of the way of everything else. */
const PREFIX = 'divepeaks.panel';

const storageKey = (scope: string, key: string) => `${PREFIX}.${scope}.${key}`;

/** Storage throws in a private window and is empty in a fresh one. */
function read(key: string): string | null {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function write(key: string, value: string) {
  try {
    sessionStorage.setItem(key, value);
  } catch {
    // Nothing to do about it: the URL still carries the view, so the page
    // works exactly as it did before anything was remembered.
  }
}

/**
 * One query-string key as state, like `useSearchParam`, **and remembered for
 * the rest of the session**: a page opened with nothing in its address comes
 * back to the view it was left on.
 *
 * Which matters for a screen somebody leaves and returns to all day. The desk
 * is on one class, filtered down to one squad, reading the round by block; they
 * step into the entry list to fix a birth date and come back — and the board
 * should be where they left it rather than back on the first tab.
 *
 * **The URL still wins whenever it says anything.** A link sent from the wall
 * is an instruction, and a shared address has to open the same view for
 * whoever receives it; only an address that is silent on a key falls back to
 * what was remembered. The restored value is then written into the address, so
 * the bar and the screen never disagree and a reload is a reload of what is
 * actually showing.
 *
 * Remembered under `scope`, which is how one round's board does not set the
 * tabs of another's: the classes a round runs are its own, and so is the block
 * somebody was standing at. The menu moves between rounds **without unmounting
 * the page**, so the scope is watched rather than read once — otherwise the
 * second round opened would inherit the first one's class and filter, which is
 * the one thing this is meant not to do. `sessionStorage` rather than
 * `localStorage` deliberately — this is where the desk got to today, not a
 * preference, and it is forgotten with the tab.
 */
export function useStickySearchParam(
  scope: string,
  key: string,
  fallback = ''
): [string, (value: string) => void] {
  const [params, setParams] = useSearchParams();
  const full = storageKey(scope, key);

  /**
   * What this board was left on, and whether it has been used yet. It is what
   * the page *opens* on, not a floor it falls back to all afternoon: the
   * moment somebody chooses for themselves it is spent, so emptying a box that
   * opened with something remembered in it stays empty.
   */
  const [opening, setOpening] = useState(() => ({ scope: full, value: read(full) }));

  // Another board under the same mounted page — the menu's row for the next
  // round. Setting state during a render is React's own answer to a prop
  // changing: it re-renders with this before anything is committed.
  if (opening.scope !== full) setOpening({ scope: full, value: read(full) });

  const inUrl = params.get(key);
  const value = inUrl ?? (opening.scope === full ? opening.value : null) ?? fallback;

  const setValue = useCallback(
    (next: string) => {
      write(full, next);
      // Chosen by hand, so what was remembered has had its say.
      setOpening({ scope: full, value: null });
      setParams(
        (current) => {
          const copy = new URLSearchParams(current);
          if (next && next !== fallback) copy.set(key, next);
          else copy.delete(key);
          return copy;
        },
        { replace: true }
      );
    },
    [key, fallback, full, setParams]
  );

  // What was remembered, put back in the address. Replacing rather than
  // pushing: coming back to a page is not a step the back button should have
  // to walk through, and the value it would land on is the one just left.
  useEffect(() => {
    if (inUrl !== null || value === fallback) return;

    setParams(
      (current) => {
        const copy = new URLSearchParams(current);
        copy.set(key, value);
        return copy;
      },
      { replace: true }
    );
  }, [inUrl, value, fallback, key, setParams]);

  return [value, setValue];
}
